import React, { useState, useEffect } from 'react';
import { Instagram } from 'lucide-react';

// Fade In Section Component
const FadeInSection = ({ children, delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = React.useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out transform ${
        isVisible 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-12'
      }`}
      style={{ 
        transitionDelay: isVisible ? `${delay}ms` : '0ms',
      }}
    >
      {children}
    </div>
  );
};

const HomePage = ({ setCurrentPage }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger fade-in animation after component mounts - much slower
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Scroll to about section
  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about-section');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to product section
  const scrollToProduct = () => {
    const productSection = document.getElementById('product-section');
    if (productSection) {
      productSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to pricing section
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Scroll to contact section
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-section');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle contact form submission
  const handleContactSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const name = formData.get('name');
    const business = formData.get('business');
    const email = formData.get('email');
    const message = formData.get('message');

    const subject = `Contact Form Submission from ${name}`;
    const body = `Name: ${name}\nBusiness: ${business}\nEmail: ${email}\n\nMessage:\n${message}`;
    
    window.location.href = `mailto:contact@checksintel.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="relative overflow-hidden">
      {/* Bold Purple Gradient Background */}
      <div className="fixed inset-0 bg-gradient-to-b from-[#4000E4] to-[#B0A6C8] -z-10"></div>

      {/* Content */}
      <div className="relative z-10">
        {/* Custom Navigation Bar for HomePage - Fixed */}
        <nav className="fixed top-0 left-0 right-0 px-6 py-6 sm:px-8 lg:px-12 z-50">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Logo */}
            <button 
              onClick={() => setCurrentPage('home')}
              className="font-extrabold text-xl sm:text-4xl hover:opacity-80 transition-opacity duration-300"
              style={{ color: '#F0EAD6', letterSpacing: '-0.02em' }}
            >
              CHECKS INTEL
            </button>

            {/* Navigation Links */}
            <div className="flex items-center space-x-6 sm:space-x-8 lg:space-x-12">
              <button 
                onClick={scrollToProduct}
                className="font-bold text-sm sm:text-base lg:text-lg hover:opacity-80 transition-opacity duration-300"
                style={{ color: '#F0EAD6' }}
              >
                Product
              </button>
              <button 
                onClick={scrollToPricing}
                className="font-bold text-sm sm:text-base lg:text-lg hover:opacity-80 transition-opacity duration-300"
                style={{ color: '#F0EAD6' }}
              >
                Pricing
              </button>
              <button 
                onClick={scrollToAbout}
                className="font-bold text-sm sm:text-base lg:text-lg hover:opacity-80 transition-opacity duration-300"
                style={{ color: '#F0EAD6' }}
              >
                About
              </button>
              <button 
                onClick={scrollToContact}
                className="font-bold text-sm sm:text-base lg:text-lg hover:opacity-80 transition-opacity duration-300"
                style={{ color: '#F0EAD6' }}
              >
                Contact
              </button>
            </div>
          </div>
        </nav>

        {/* Hero Section - Full Viewport Height */}
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full">
            <h1 
              className={`font-extrabold text-center leading-tight transition-all duration-[3500ms] ease-out ${
                isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'
              }`}
              style={{
                fontSize: 'clamp(3rem, 15vw, 10rem)',
                letterSpacing: '-0.07em',
                color: '#F0EAD6'
              }}
            >
              SPLIT HAPPENS
            </h1>
          </div>
        </div>

        {/* Product Section - Meet Checkie */}
        <div id="product-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <FadeInSection>
            <h2 className="text-5xl sm:text-6xl md:text-7xl font-semibold mb-16 sm:mb-24 text-center leading-tight" style={{ color: '#F0EAD6', letterSpacing: '-0.03em' }}>
              Meet checkie
            </h2>
          </FadeInSection>

          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Editable Text */}
            <FadeInSection delay={200}>
              <div className="space-y-6">
                <p className="text-xl sm:text-2xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                  Your pocket-sized financial companion that makes splitting bills as simple as snapping a photo.
                </p>
                
                <p className="text-lg sm:text-xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                  Scan receipts, split expenses instantly, and keep track of who owes what—all in one beautifully designed app. 
                  No more awkward money conversations or spreadsheet headaches.
                </p>

                <p className="text-lg sm:text-xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                  Whether you're splitting dinner with friends, managing group trips, or tracking shared household expenses, 
                  checkie keeps everything transparent and stress-free.
                </p>

                <div className="pt-6">
                  <button 
                    onClick={() => setCurrentPage('checkie')}
                    className="group inline-flex items-center px-8 py-4 bg-[#F0EAD6] font-light rounded-full text-lg shadow-xl transition-all duration-500 ease-out transform hover:scale-105 tracking-wide"
                    style={{ color: '#4000E4' }}
                  >
                    <span className="relative z-10">Learn more</span>
                    <svg className="ml-3 w-5 h-5 transform group-hover:translate-x-2 transition-all duration-500 ease-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </FadeInSection>

            {/* Right Column - iPhone Mockup */}
            <FadeInSection delay={400}>
              <div className="flex justify-center md:justify-end">
                <div className="relative">
                  {/* iPhone Frame */}
                  <div className="relative w-[240px] sm:w-[260px] lg:w-[280px]" style={{ aspectRatio: '9/19.5' }}>
                    {/* Phone Border/Frame */}
                    <div className="absolute inset-0 rounded-[3rem] bg-black shadow-2xl">
                      {/* Screen Area */}
                      <div className="absolute inset-[12px] rounded-[2.7rem] bg-white overflow-hidden">
                        {/* Notch */}
                        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[35%] h-[25px] bg-black rounded-b-3xl z-10"></div>
                        
                        {/* Screenshot Goes Here - Replace with your app screenshot */}
                        <img 
                          src="/path-to-your-screenshot.png" 
                          alt="Checkie App Screenshot"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    
                    {/* Phone Buttons */}
                    <div className="absolute -right-[2px] top-[120px] w-[3px] h-[60px] bg-black rounded-r-sm"></div>
                    <div className="absolute -left-[2px] top-[100px] w-[3px] h-[32px] bg-black rounded-l-sm"></div>
                    <div className="absolute -left-[2px] top-[145px] w-[3px] h-[60px] bg-black rounded-l-sm"></div>
                    <div className="absolute -left-[2px] top-[215px] w-[3px] h-[60px] bg-black rounded-l-sm"></div>
                  </div>
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>

        {/* Pricing Section */}
        <div id="pricing-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <FadeInSection>
            <h2 className="text-5xl sm:text-6xl md:text-7xl font-semibold mb-8 text-center leading-tight" style={{ color: '#F0EAD6', letterSpacing: '-0.03em' }}>
              Pricing
            </h2>
            <p className="text-lg sm:text-xl text-center mb-16 sm:mb-24 font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
              Choose the plan that's right for you
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
            {/* Free Tier */}
            <FadeInSection delay={200}>
              <div className="group bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-8 sm:p-10 transition-all duration-500 border border-white border-opacity-20 hover:bg-[#F0EAD6] hover:scale-105 hover:shadow-2xl relative">
                <h3 className="text-2xl sm:text-3xl font-light mb-2 tracking-wide text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">
                  Free
                </h3>
                <div className="mb-8">
                  <span className="text-5xl sm:text-6xl font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">$0</span>
                  <span className="text-lg font-light ml-2 text-[#F0EAD6] opacity-70 group-hover:!text-[#4000E4] transition-colors duration-500">/month</span>
                </div>
                
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Unlimited receipt scanning</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Up to 5 active groups</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Basic expense tracking</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">30-day history</span>
                  </li>
                </ul>

                <button className="w-full py-4 px-6 rounded-full font-light text-base transition-all duration-500 shadow-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-[#F0EAD6] group-hover:bg-[#4000E4] group-hover:border-[#4000E4] group-hover:!text-[#F0EAD6]">
                  Get Started
                </button>
              </div>
            </FadeInSection>

            {/* Pro Tier */}
            <FadeInSection delay={300}>
              <div className="group bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-8 sm:p-10 transition-all duration-500 border border-white border-opacity-20 hover:bg-[#F0EAD6] hover:scale-105 hover:shadow-2xl relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-[#4000E4] px-6 py-2 rounded-full">
                  <span className="text-sm font-light tracking-wide text-[#F0EAD6]">Most Popular</span>
                </div>
                
                <h3 className="text-2xl sm:text-3xl font-light mb-2 tracking-wide text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">
                  Pro
                </h3>
                <div className="mb-8">
                  <span className="text-5xl sm:text-6xl font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">$4.99</span>
                  <span className="text-lg font-light ml-2 text-[#F0EAD6] opacity-70 group-hover:!text-[#4000E4] transition-colors duration-500">/month</span>
                </div>
                
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Everything in Free</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Unlimited active groups</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Advanced analytics & insights</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Unlimited history</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Priority support</span>
                  </li>
                </ul>

                <button className="w-full py-4 px-6 rounded-full font-light text-base transition-all duration-500 shadow-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-[#F0EAD6] group-hover:bg-[#4000E4] group-hover:border-[#4000E4] group-hover:!text-[#F0EAD6]">
                  Start Free Trial
                </button>
              </div>
            </FadeInSection>

            {/* Enterprise Tier */}
            <FadeInSection delay={400}>
              <div className="group bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-8 sm:p-10 transition-all duration-500 border border-white border-opacity-20 hover:bg-[#F0EAD6] hover:scale-105 hover:shadow-2xl relative">
                <h3 className="text-2xl sm:text-3xl font-light mb-2 tracking-wide text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">
                  Enterprise
                </h3>
                <div className="mb-8">
                  <span className="text-5xl sm:text-6xl font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Custom</span>
                </div>
                
                <ul className="space-y-4 mb-10">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Everything in Pro</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Custom integrations</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Dedicated account manager</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">Advanced security features</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0 stroke-[#F0EAD6] group-hover:!stroke-[#4000E4] transition-colors duration-500" fill="none" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-base font-light text-[#F0EAD6] group-hover:!text-[#4000E4] transition-colors duration-500">SLA guarantee</span>
                  </li>
                </ul>

                <button className="w-full py-4 px-6 rounded-full font-light text-base transition-all duration-500 shadow-lg bg-white bg-opacity-20 border border-white border-opacity-30 text-[#F0EAD6] group-hover:bg-[#4000E4] group-hover:border-[#4000E4] group-hover:!text-[#F0EAD6]">
                  Contact Sales
                </button>
              </div>
            </FadeInSection>
          </div>
        </div>

        {/* Content Sections */}
        <div id="about-section" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <FadeInSection>
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-4xl sm:text-5xl md:text-6xl font-semibold mb-12 sm:mb-16 leading-tight" style={{ color: '#F0EAD6', letterSpacing: '-0.03em' }}>
                Bringing People and Teams Together
              </h2>
            </div>
          </FadeInSection>

          <FadeInSection delay={200}>
            <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-10">
              <p className="text-lg sm:text-xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                Our mission is straightforward: make shared expenses easy, clear, and fair.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={300}>
            <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-10">
              <p className="text-lg sm:text-xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                Whether it's a lunch with friends, a client dinner, a group trip, or everyday shared costs, 
                checkie helps people and organizations manage expenses without friction.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={400}>
            <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-10">
              <p className="text-lg sm:text-xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                We believe money conversations should be simple and professional, not uncomfortable. Our platform enables groups 
                to split bills in real time, assign costs collaboratively, and track expenses transparently — all while keeping financial data private.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={500}>
            <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-10">
              <p className="text-lg sm:text-xl leading-relaxed font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                From businesses coordinating shared spending to friends and families managing everyday expenses, 
                checkie turns clarity and accountability into better decision-making.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={600}>
            <div className="text-center mb-12 sm:mb-16 mt-12 sm:mt-16">
              <p className="text-lg sm:text-xl mb-8 sm:mb-10 font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                Join a growing community building smarter, more confident money habits together
              </p>
              
              <button 
                onClick={() => setCurrentPage('checkie')}
                className="group relative inline-flex items-center px-8 sm:px-12 py-4 sm:py-5 bg-[#F0EAD6] font-light rounded-full text-base sm:text-lg shadow-2xl transition-all duration-700 ease-out transform hover:scale-110 hover:-translate-y-2 tracking-wide"
                style={{ color: '#4000E4' }}
              >
                <span className="relative z-10 transition-all duration-500 ease-out">Try checkie</span>
                <svg className="ml-3 sm:ml-4 w-4 h-4 sm:w-5 sm:h-5 transform group-hover:translate-x-2 transition-all duration-500 ease-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </button>
            </div>
          </FadeInSection>
        </div>

        {/* Contact Section */}
        <div id="contact-section" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <FadeInSection>
            <h2 className="text-5xl sm:text-6xl md:text-7xl font-semibold mb-8 text-center leading-tight" style={{ color: '#F0EAD6', letterSpacing: '-0.03em' }}>
              Get in Touch
            </h2>
            <p className="text-lg sm:text-xl text-center mb-16 sm:mb-20 font-light" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
              Have questions? We'd love to hear from you.
            </p>
          </FadeInSection>

          <FadeInSection delay={200}>
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-3xl p-8 sm:p-12 border border-white border-opacity-20">
              <form className="space-y-6" onSubmit={handleContactSubmit}>
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-light mb-2" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full px-4 py-3 rounded-xl bg-white bg-opacity-20 border border-white border-opacity-30 focus:outline-none focus:border-opacity-50 transition-all duration-300 font-light"
                    style={{ color: '#F0EAD6' }}
                    placeholder="John Doe"
                  />
                </div>

                {/* Business Name */}
                <div>
                  <label htmlFor="business" className="block text-sm font-light mb-2" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                    Business Name
                  </label>
                  <input
                    type="text"
                    id="business"
                    name="business"
                    className="w-full px-4 py-3 rounded-xl bg-white bg-opacity-20 border border-white border-opacity-30 focus:outline-none focus:border-opacity-50 transition-all duration-300 font-light"
                    style={{ color: '#F0EAD6' }}
                    placeholder="Your Company"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-light mb-2" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="w-full px-4 py-3 rounded-xl bg-white bg-opacity-20 border border-white border-opacity-30 focus:outline-none focus:border-opacity-50 transition-all duration-300 font-light"
                    style={{ color: '#F0EAD6' }}
                    placeholder="john@example.com"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-light mb-2" style={{ color: '#F0EAD6', letterSpacing: '-0.01em' }}>
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="6"
                    className="w-full px-4 py-3 rounded-xl bg-white bg-opacity-20 border border-white border-opacity-30 focus:outline-none focus:border-opacity-50 transition-all duration-300 font-light resize-none"
                    style={{ color: '#F0EAD6' }}
                    placeholder="Tell us about your project or question..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    className="w-full py-4 px-6 bg-[#F0EAD6] hover:bg-opacity-90 rounded-full font-light text-lg transition-all duration-500 shadow-xl transform hover:scale-105"
                    style={{ color: '#4000E4' }}
                  >
                    Send Message
                  </button>
                </div>
              </form>
            </div>
          </FadeInSection>
        </div>

        {/* Footer */}
        <div className="mt-auto">
          <FadeInSection delay={800}>
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 sm:pb-12">
              <div className="pt-8 sm:pt-12 text-center" style={{ borderTop: '1px solid rgba(240, 234, 214, 0.3)' }}>
                <div className="mb-4 sm:mb-6">
                  <div className="inline-flex items-center space-x-6 sm:space-x-8">
                    <div className="h-px w-20 sm:w-28" style={{ background: 'linear-gradient(to right, transparent, rgba(240, 234, 214, 0.5))' }}></div>
                    <span className="text-base sm:text-lg font-extralight tracking-wider" style={{ color: '#4000E4' }}>Built with passion by</span>
                    <div className="h-px w-20 sm:w-28" style={{ background: 'linear-gradient(to left, transparent, rgba(240, 234, 214, 0.5))' }}></div>
                  </div>
                </div>
                <p className="text-lg sm:text-xl font-light tracking-wide mb-4" style={{ color: '#4000E4' }}>
                  the Checks Intel team
                </p>
                
                {/* Social Media and Contact Section */}
                <div className="mb-6 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                  <a 
                    href="https://www.instagram.com/getcheckie/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center hover:opacity-80 transition-all duration-300 ease-out font-light tracking-wide text-sm sm:text-base hover:underline decoration-2 underline-offset-4 group"
                    style={{ color: '#4000E4' }}
                  >
                    <Instagram className="h-4 w-4 sm:h-5 sm:w-5 mr-2 group-hover:scale-110 transition-all duration-300" />
                    @getcheckie
                  </a>
                  
                  <div className="w-px h-4 hidden sm:block" style={{ backgroundColor: 'rgba(240, 234, 214, 0.3)' }}></div>
                  <span className="text-sm sm:hidden" style={{ color: 'rgba(240, 234, 214, 0.4)' }}>•</span>
                  
                  <a 
                    href="mailto:contact@checksintel.com"
                    className="hover:opacity-80 transition-all duration-300 ease-out font-light tracking-wide text-sm sm:text-base hover:underline decoration-2 underline-offset-4"
                    style={{ color: '#4000E4' }}
                  >
                    contact@checksintel.com
                  </a>
                </div>
                
                <p className="text-xs sm:text-sm font-light tracking-wide" style={{ color: '#4000E4', opacity: 0.6 }}>
                  © 2025 Checks Intel Inc. All rights reserved.
                </p>
              </div>
            </div>
          </FadeInSection>
        </div>
      </div>
    </div>
  );
};

export default HomePage;